import type { DBSchema, IDBPDatabase } from 'idb';
import { openDB } from 'idb';

import type { IndexKind, IndexingProgress, TreeEventScopeId } from './types';

const DB_VERSION = 1;

const dbName = (userId: string) => `search:${userId}`;

export interface TreeEventScopeSubscription {
    treeEventScopeId: TreeEventScopeId;
    lastEventId: string;
    lastEventIdTime: number;
}

export interface IndexPopulatorState {
    uid: string;
    indexKind: IndexKind;
    indexPopulatorKind: string;
    treeEventScopeId: TreeEventScopeId;
    generation: number;
    version: number;
    done: boolean;
    progress: IndexingProgress;
}

interface SearchDBSchema extends DBSchema {
    indexBlobs: {
        key: [string, string]; // [indexKind, blobName]
        value: ArrayBuffer;
    };
    treeEventScopeSubscriptions: {
        key: TreeEventScopeId;
        value: TreeEventScopeSubscription;
    };
    indexPopulatorStates: {
        key: string;
        value: IndexPopulatorState;
    };
    userSettings: {
        key: string;
        value: string | boolean;
    };
}

type RawSearchDB = IDBPDatabase<SearchDBSchema>;

/**
 * `indexBlobs` holds blobs for every index kind in a single object store, keyed by
 * `[indexKind, blobName]`. To select rows for a single `indexKind`, we use a
 * compound-key prefix scan: arrays sort after strings in IndexedDB, so
 * `[indexKind, []]` sits just above every `[indexKind, <any string>]`.
 */
const indexBlobsKeyRangeForKind = (indexKind: IndexKind): IDBKeyRange =>
    IDBKeyRange.bound([indexKind, ''], [indexKind, []]);

/**
 * Encapsulates all IndexedDB operations for the search module.
 * One database per user: "search:<userId>".
 * IndexedDB is the persistent store — IndexBlobStore reads from here with a pending-write buffer on top.
 */
export class SearchDB {
    private constructor(private readonly db: RawSearchDB) {}

    static async open(userId: string): Promise<SearchDB> {
        const db = await openDB<SearchDBSchema>(dbName(userId), DB_VERSION, {
            upgrade(database, oldVersion) {
                if (oldVersion < 1) {
                    database.createObjectStore('indexBlobs');
                    database.createObjectStore('treeEventScopeSubscriptions');
                    database.createObjectStore('indexPopulatorStates');
                    database.createObjectStore('userSettings');
                }
            },
        });
        return new SearchDB(db);
    }

    // --- Index blobs (keyed by [indexKind, blobName]) ---

    async getDecryptedIndexBlob(
        key: [string, string],
        decrypt: (ciphertext: ArrayBuffer) => Promise<ArrayBuffer>
    ): Promise<ArrayBuffer | undefined> {
        const raw = await this.db.get('indexBlobs', key);
        if (raw === undefined) {
            return undefined;
        }
        return decrypt(raw);
    }

    async putEncryptedIndexBlob(
        key: [string, string],
        plaintext: ArrayBuffer,
        encrypt: (data: ArrayBuffer) => Promise<ArrayBuffer>
    ): Promise<[string, string]> {
        const encrypted = await encrypt(plaintext);
        return this.db.put('indexBlobs', encrypted, key);
    }

    deleteIndexBlob(key: [string, string]): Promise<void> {
        return this.db.delete('indexBlobs', key);
    }

    async getAllIndexBlobKeys(): Promise<[string, string][]> {
        return this.db.getAllKeys('indexBlobs');
    }

    /** Count the number of blobs persisted under `indexKind`. */
    async countIndexBlobs(indexKind: IndexKind): Promise<number> {
        return this.db.count('indexBlobs', indexBlobsKeyRangeForKind(indexKind));
    }

    /**
     * Sum the ciphertext byte size of every blob stored under `indexKind`.
     * Used by diagnostics to report per-index on-disk usage.
     */
    async getIndexBlobsByteSize(indexKind: IndexKind): Promise<number> {
        const blobs = await this.db.getAll('indexBlobs', indexBlobsKeyRangeForKind(indexKind));
        let total = 0;
        for (const blob of blobs) {
            total += blob.byteLength;
        }
        return total;
    }

    // --- Tree event scope subscriptions ---

    getSubscription(treeEventScopeId: TreeEventScopeId): Promise<TreeEventScopeSubscription | undefined> {
        return this.db.get('treeEventScopeSubscriptions', treeEventScopeId);
    }

    getAllSubscriptions(): Promise<TreeEventScopeSubscription[]> {
        return this.db.getAll('treeEventScopeSubscriptions');
    }

    putSubscription(subscription: TreeEventScopeSubscription): Promise<TreeEventScopeId> {
        return this.db.put('treeEventScopeSubscriptions', subscription, subscription.treeEventScopeId);
    }

    deleteSubscription(treeEventScopeId: TreeEventScopeId): Promise<void> {
        return this.db.delete('treeEventScopeSubscriptions', treeEventScopeId);
    }

    // --- Index populator states ---

    getPopulatorState(uid: string): Promise<IndexPopulatorState | undefined> {
        return this.db.get('indexPopulatorStates', uid);
    }

    getAllPopulatorStates(): Promise<IndexPopulatorState[]> {
        return this.db.getAll('indexPopulatorStates');
    }

    putPopulatorState(state: IndexPopulatorState): Promise<string> {
        return this.db.put('indexPopulatorStates', state, state.uid);
    }

    deletePopulatorState(uid: string): Promise<void> {
        return this.db.delete('indexPopulatorStates', uid);
    }

    // --- User preferences & config ---

    async getSearchCryptoKey(decrypt: (ciphertext: string) => Promise<string>): Promise<string | undefined> {
        const value = await this.db.get('userSettings', 'searchCryptoKey');
        if (typeof value !== 'string') {
            return undefined;
        }
        return decrypt(value);
    }

    async putSearchCryptoKey(plaintext: string, encrypt: (data: string) => Promise<string>): Promise<string> {
        const encrypted = await encrypt(plaintext);
        return this.db.put('userSettings', encrypted, 'searchCryptoKey');
    }

    async isOptedIn(): Promise<boolean> {
        const value = await this.db.get('userSettings', 'optIn');
        return value === true;
    }

    setOptedIn(): Promise<string> {
        return this.db.put('userSettings', true, 'optIn');
    }

    // Clear index blobs, subscriptions and populator states so the indexer rebuilds from scratch.
    // Used when the encryption key is regenerated (e.g. after key rotation).
    async clearIndex(): Promise<void> {
        await this.db.clear('indexBlobs');
        await this.db.clear('treeEventScopeSubscriptions');
        await this.db.clear('indexPopulatorStates');
    }

    /**
     * Clear all data from every object store. The DB and connections remain open.
     *
     * We clear stores instead of deleting the DB because deleteDB blocks indefinitely
     * while any connection is open. Multiple connections can exist simultaneously
     * (SharedWorker + one per open tab), and coordinating their closure is non-trivial.
     * Clearing doesn't require closing connections and achieves the same result.
     */
    async clear(): Promise<void> {
        const storeNames = [...this.db.objectStoreNames];
        const tx = this.db.transaction(storeNames, 'readwrite');
        await Promise.all([...storeNames.map((name) => tx.objectStore(name).clear()), tx.done]);
    }

    close(): void {
        this.db.close();
    }
}
